/**
 * MyFinance — FM1 Student Self-Finance Page
 *
 * Route: /my-finance
 * RBAC: finance.account.view.own (student role only)
 *
 * FM1-G1 PROVEN: student_id resolved via JWT email crosswalk.
 * Student cannot supply a different student_id.
 *
 * FM1 LIVE TABS:
 *   Balance   — summary card
 *   Charges   — itemized charges from canonical PG
 *   Payments  — payment history from canonical PG
 *
 * COMING SOON TABS (FM2+):
 *   Invoices, Receipts, Installments, Scholarships, Refunds
 *
 * A01 Statement download: GET /api/student/me/finance/statement?format=pdf
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Download, Loader2, AlertCircle, Wallet, CreditCard,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MoneyAmount, MoneyBadge } from "../components/finance/MoneyAmount";
import { FinanceCapabilityState } from "../components/finance/FinanceCapabilityState";
import { PageHeader } from "../components/layout/Shell";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import StudentSponsorshipsView from "./StudentSponsorshipsView";
import StudentReimbursementsView from "./StudentReimbursementsView";
// FM7 BOUND-REOPEN-FM7-03: Pay Now capability gate (ACTIVE gateway required)
import { useIntegrationStatus } from "../hooks/useIntegrationStatus";

const STATUS_COLORS = {
  FINALIZED: "bg-slate-700 text-slate-200",
  CHARGED:   "bg-blue-900/50 text-blue-300",
  PAID:      "bg-emerald-900/50 text-emerald-300",
  PARTIALLY_PAID: "bg-amber-900/50 text-amber-300",
  OUTSTANDING: "bg-red-900/40 text-red-300",
  UNAPPLIED: "bg-purple-900/40 text-purple-300",
  POSTED:    "bg-emerald-900/50 text-emerald-300",
  CANCELLED: "bg-slate-800 text-slate-500",
  CREDITED:  "bg-cyan-900/40 text-cyan-300",
  REFUNDED:  "bg-orange-900/40 text-orange-300",
};

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || "bg-slate-800 text-slate-400";
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {status}
    </span>
  );
}

const TABS = [
  { id: "balance",       label: "Balance",       live: true  },
  { id: "charges",       label: "Charges",       live: true  },
  { id: "payments",      label: "Payments",      live: true  },
  { id: "invoices",      label: "Invoices",      live: false, wave: "FM2" },
  { id: "receipts",      label: "Receipts",      live: false, wave: "FM2" },
  { id: "installments",  label: "Installments",  live: false, wave: "FM2" },
  { id: "scholarships",  label: "Scholarships",  live: false, wave: "FM4" },
  { id: "sponsorships",  label: "Sponsorships",  live: true               },
  { id: "reimbursements", label: "Reimbursements", live: true              },
  // FM5: Refunds, Credits, Collections Notice (BOUND-REOPEN-FM5-03)
  { id: "refunds",       label: "Refunds",       live: true               },
  { id: "credits",       label: "Credits",       live: true               },
  { id: "collections",   label: "Collections",   live: true               },
];

export default function MyFinance() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("balance");
  const [summary, setSummary] = useState(null);
  const [charges, setCharges] = useState(null);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isStudent = user?.role === "student";

  // FM7 BOUND-REOPEN-FM7-03: Pay Now capability gate
  // Only ACTIVE payment gateway enables this button (INV-FM7-FOUND-01)
  const { isActive: integrationActive, statusOf: integrationStatus } = useIntegrationStatus();
  const gatewayActive = integrationActive("PAYMENTS");

  useEffect(() => {
    if (!isStudent) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get("/student/me/finance"),
      api.get("/student/me/finance/charges"),
      api.get("/student/me/finance/payments"),
    ])
      .then(([s, c, p]) => {
        setSummary(s.data);
        setCharges(c.data);
        setPayments(p.data);
      })
      .catch((e) => {
        const detail = e?.response?.data;
        if (typeof detail === "object" && detail?.code) {
          setError(detail.message || JSON.stringify(detail));
        } else {
          setError("Failed to load your Finance account.");
        }
      })
      .finally(() => setLoading(false));
  }, [isStudent]);

  const handleDownload = async () => {
    try {
      const resp = await api.get("/student/me/finance/statement?format=pdf", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([resp.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `my_fee_statement_${new Date().getFullYear()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "A01_PDF_RENDERER_ENVIRONMENT_BLOCKED") {
        alert("PDF is not available in this environment. Contact Finance for a printed statement.");
      } else {
        alert("Download failed. Please try again.");
      }
    }
  };

  if (!isStudent) {
    return (
      <div className="space-y-6" data-testid="my-finance-page">
        <PageHeader title="My Finance" />
        <FinanceCapabilityState
          state="NOT_AVAILABLE"
          title="Student Access Only"
          reason="This page is accessible only to student accounts. Staff should use Finance → Fees & Student Accounts."
          data-testid="my-finance-not-student"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-finance-page">
      <div className="flex items-center justify-between">
        <PageHeader
          title="My Finance"
          description="Your canonical fee account — charges, payments, and balance."
        />
        {summary && (
          <button
            data-testid="my-finance-download-statement"
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-900/40 hover:bg-blue-800/50 border border-blue-700/40 text-blue-300 text-xs font-medium transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Download Statement
          </button>
        )}
      </div>

      {loading && (
        <div
          data-testid="my-finance-loading"
          className="flex items-center justify-center py-16 text-slate-500"
        >
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading your Finance account…
        </div>
      )}

      {error && !loading && (
        <div
          data-testid="my-finance-error"
          className="flex items-start gap-3 p-4 rounded-lg bg-red-950/30 border border-red-900/40 text-red-300 text-sm"
        >
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Finance account unavailable</p>
            <p className="text-xs text-red-400/80 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {summary && !loading && (
        <>
          {/* Balance Header Card */}
          <div
            data-testid="my-finance-balance-card"
            className="p-5 rounded-xl bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <div className="p-3 rounded-full bg-blue-900/30 border border-blue-800/40">
              <Wallet className="h-6 w-6 text-blue-400" />
            </div>
            <div className="flex-1 space-y-1">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Outstanding Balance</p>
              <MoneyAmount
                amount={summary.outstanding_balance}
                currency={summary.currency}
                size="2xl"
                data-testid="my-finance-outstanding-balance"
              />
              {parseFloat(summary.unapplied_cash || 0) > 0 && (
                <p className="text-xs text-blue-400">
                  +{" "}
                  <MoneyBadge
                    amount={summary.unapplied_cash}
                    currency={summary.currency}
                    variant="unapplied"
                  />{" "}
                  unapplied cash on account
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 text-center">
              <div className="px-4 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50">
                <p className="text-[10px] text-slate-500">Charges</p>
                <MoneyAmount
                  amount={summary.total_finalized_charges}
                  currency={summary.currency}
                  size="sm"
                  data-testid="my-finance-total-charges"
                />
              </div>
              <div className="px-4 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50">
                <p className="text-[10px] text-slate-500">Paid</p>
                <MoneyAmount
                  amount={summary.total_posted_payments}
                  currency={summary.currency}
                  size="sm"
                  data-testid="my-finance-total-payments"
                />
              </div>
            </div>
          </div>

          {/* FM7 BOUND-REOPEN-FM7-03: Pay Now capability surface
              Enabled ONLY when PAYMENTS integration is ACTIVE (INV-FM7-FOUND-01)
              CONFIGURED or absent → NOT_CONFIGURED widget, no dead button */}
          <div data-testid="fm7-pay-now-surface">
            {gatewayActive ? (
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-emerald-900/20 border border-emerald-800/30">
                <div className="flex items-center gap-2 text-sm text-emerald-300">
                  <CreditCard className="h-4 w-4" />
                  <span>Online payment is available for your account.</span>
                </div>
                <button
                  data-testid="pay-now-btn"
                  onClick={() => alert("Payment gateway integration is active. Connect a real provider to complete payment flow.")}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium transition-colors"
                >
                  <CreditCard className="h-3.5 w-3.5" />
                  Pay Now
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-slate-900/40 border border-slate-700/30 text-xs text-slate-500"
                data-testid="pay-now-not-available">
                <CreditCard className="h-3.5 w-3.5 opacity-40 shrink-0" />
                <span>
                  Online payment not available.
                  {integrationStatus("PAYMENTS") === "CONFIGURED"
                    ? " Gateway is configured but not yet active — contact your finance administrator."
                    : " No payment gateway configured for this institution."}
                </span>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div
            className="overflow-x-auto"
            data-testid="my-finance-tabs"
          >
            <div className="border-b border-slate-800 flex gap-1 min-w-max">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  data-testid={`my-finance-tab-${tab.id}`}
                  onClick={() => tab.live && setActiveTab(tab.id)}
                  disabled={!tab.live}
                  className={`px-4 py-2 text-xs font-medium capitalize transition-colors border-b-2 -mb-px whitespace-nowrap ${
                    !tab.live
                      ? "border-transparent text-slate-600 cursor-not-allowed"
                      : activeTab === tab.id
                      ? "border-blue-500 text-blue-400"
                      : "border-transparent text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {tab.label}
                  {!tab.live && (
                    <span className="ml-1 text-[9px] text-slate-700 uppercase">{tab.wave}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Panels */}
          {activeTab === "balance" && (
            <BalanceTab summary={summary} />
          )}
          {activeTab === "charges" && (
            <ChargesTab
              items={charges?.items || []}
              currency={summary.currency}
            />
          )}
          {activeTab === "payments" && (
            <PaymentsTab
              items={payments?.items || []}
              currency={summary.currency}
            />
          )}
          {activeTab === "sponsorships" && (
            <StudentSponsorshipsView embedded />
          )}
          {activeTab === "reimbursements" && (
            <StudentReimbursementsView />
          )}
          {/* FM5: Student Refund Status (BOUND-REOPEN-FM5-03) */}
          {activeTab === "refunds" && (
            <StudentRefundsPanel institutionId={user?.institution_id} />
          )}
          {/* FM5: Student Credits Panel (BOUND-REOPEN-FM5-03) */}
          {activeTab === "credits" && (
            <StudentFM5Panel institutionId={user?.institution_id} tab="credits" />
          )}
          {/* FM5: Student Collections Notice (BOUND-REOPEN-FM5-03) */}
          {activeTab === "collections" && (
            <StudentFM5Panel institutionId={user?.institution_id} tab="collections" />
          )}
          {!["balance","charges","payments","sponsorships","reimbursements","refunds","credits","collections"].includes(activeTab) && !TABS.find((t) => t.id === activeTab)?.live && (
            <FinanceCapabilityState
              state="COMING_SOON"
              title={TABS.find((t) => t.id === activeTab)?.label}
              reason="This capability is not yet available. It will be introduced in a future Finance wave."
              data-testid={`my-finance-${activeTab}-coming-soon`}
            />
          )}
        </>
      )}
    </div>
  );
}

function BalanceTab({ summary }) {
  const rows = [
    ["Account Status", summary.account_status],
    ["Currency", summary.currency],
    ["Total Finalized Charges", <MoneyBadge key="c" amount={summary.total_finalized_charges} currency={summary.currency} />],
    ["Active Allocations", <MoneyBadge key="a" amount={summary.total_active_allocations} currency={summary.currency} variant="paid" />],
    ["Applied Credits", <MoneyBadge key="cr" amount={summary.total_applied_credits} currency={summary.currency} variant="paid" />],
    ["Outstanding Balance", <MoneyBadge key="o" amount={summary.outstanding_balance} currency={summary.currency} variant="outstanding" />],
    ["Unapplied Cash", <MoneyBadge key="u" amount={summary.unapplied_cash} currency={summary.currency} variant="unapplied" />],
    ["As of", summary.as_of ? summary.as_of.replace("T", " ").slice(0, 19) + " UTC" : "—"],
  ];
  return (
    <table className="w-full text-xs" data-testid="my-finance-balance-table">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label} className="border-b border-slate-800/50">
            <td className="py-2 pr-4 text-slate-500 font-medium w-48">{label}</td>
            <td className="py-2 text-slate-200">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ChargesTab({ items, currency }) {
  if (items.length === 0) {
    return (
      <FinanceCapabilityState
        state="NOT_AVAILABLE"
        title="No Charges"
        reason="No finalized charges are recorded on your account yet."
        data-testid="my-finance-charges-empty"
      />
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="my-finance-charges-table">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800">
            <TableHead className="text-slate-500 text-xs">Date</TableHead>
            <TableHead className="text-slate-500 text-xs">Description</TableHead>
            <TableHead className="text-slate-500 text-xs">Status</TableHead>
            <TableHead className="text-slate-500 text-xs text-right">Amount</TableHead>
            <TableHead className="text-slate-500 text-xs text-right">Outstanding</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.id} className="border-slate-800/50">
              <TableCell className="text-xs text-slate-400">{(c.charge_date || "—").slice(0, 10)}</TableCell>
              <TableCell className="text-xs text-slate-300 max-w-[200px] truncate">{c.description || "Fee Charge"}</TableCell>
              <TableCell><StatusBadge status={c.status} /></TableCell>
              <TableCell className="text-right">
                <MoneyAmount amount={c.original_amount} currency={currency} size="sm" />
              </TableCell>
              <TableCell className="text-right">
                <MoneyAmount amount={c.open_amount} currency={currency} size="sm" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PaymentsTab({ items, currency }) {
  if (items.length === 0) {
    return (
      <FinanceCapabilityState
        state="NOT_AVAILABLE"
        title="No Payments"
        reason="No payments have been recorded on your account yet."
        data-testid="my-finance-payments-empty"
      />
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="my-finance-payments-table">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800">
            <TableHead className="text-slate-500 text-xs">Date</TableHead>
            <TableHead className="text-slate-500 text-xs">Method</TableHead>
            <TableHead className="text-slate-500 text-xs">Reference</TableHead>
            <TableHead className="text-slate-500 text-xs">Status</TableHead>
            <TableHead className="text-slate-500 text-xs text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((p) => (
            <TableRow key={p.id} className="border-slate-800/50">
              <TableCell className="text-xs text-slate-400">{(p.payment_date || "—").slice(0, 10)}</TableCell>
              <TableCell className="text-xs text-slate-300">{p.payment_method}</TableCell>
              <TableCell className="text-xs text-slate-500">{p.reference_number || "—"}</TableCell>
              <TableCell><StatusBadge status={p.status} /></TableCell>
              <TableCell className="text-right">
                <MoneyAmount amount={p.payment_amount} currency={currency} size="sm" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// FM5 Student Panels (BOUND-REOPEN-FM5-03)
// ─────────────────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fm5AuthHeaders() {
  const t = localStorage.getItem("academiaos_token");
  return { Authorization: `Bearer ${t}` };
}

const fmtMoney = (v) => {
  const n = parseFloat(v || 0);
  return isNaN(n) ? "₹0.00" : `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};

// ── Student Refunds Panel ─────────────────────────────────────────────────────
function StudentRefundsPanel({ institutionId }) {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/student/me/finance/fm5/refunds?institution_id=${institutionId}`,
        { headers: fm5AuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setRefunds(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  const STATUS_COLORS = {
    INITIATED:    "text-slate-400",
    UNDER_REVIEW: "text-amber-400",
    APPROVED:     "text-emerald-400",
    REJECTED:     "text-red-400",
    WITHDRAWN:    "text-slate-500",
  };

  if (loading) return <div className="text-slate-400 text-sm py-8 text-center">Loading refunds…</div>;
  if (error) return <div className="text-red-400 text-sm py-4">{error}</div>;
  if (!refunds.length) return (
    <div className="text-slate-500 text-sm py-8 text-center" data-testid="student-refunds-empty">
      No refund requests found for your account.
    </div>
  );

  return (
    <div className="space-y-2" data-testid="student-refunds-panel">
      <p className="text-xs text-slate-500">
        Your refund requests. A refund is a separate financial event — it does not alter your payment history.
      </p>
      {refunds.map(r => (
        <div key={r.id} data-testid={`student-refund-item-${r.id}`}
          className="flex items-center justify-between bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2">
          <div className="space-y-0.5">
            <div className={`text-xs font-medium ${STATUS_COLORS[r.status] || "text-slate-400"}`}>{r.status?.replace(/_/g," ")}</div>
            <div className="text-xs text-slate-500">{r.reason_code?.replace(/_/g," ")}</div>
          </div>
          <div className="text-sm font-semibold text-slate-200">{fmtMoney(r.requested_amount)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Student FM5 Summary Panel (Credits + Collections) ────────────────────────
function StudentFM5Panel({ institutionId, tab }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/student/me/finance/fm5/summary?institution_id=${institutionId}`,
        { headers: fm5AuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setSummary(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-slate-400 text-sm py-8 text-center">Loading…</div>;
  if (error) return <div className="text-red-400 text-sm py-4">{error}</div>;

  // ── Credits sub-panel ──────────────────────────────────────────────────────
  if (tab === "credits") {
    const credits = summary?.fm5_credits || [];
    if (!credits.length) return (
      <div className="text-slate-500 text-sm py-8 text-center" data-testid="student-credits-empty">
        No credit memos applied to your account.
      </div>
    );
    return (
      <div className="space-y-2" data-testid="student-credits-panel">
        <p className="text-xs text-slate-500">
          Credit memos reduce your assessed liability. These are FM5 adjustment credits.
        </p>
        {credits.map(c => (
          <div key={c.id} data-testid={`student-credit-item-${c.id}`}
            className="flex items-center justify-between bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2">
            <div className="space-y-0.5">
              <div className="text-xs font-medium text-emerald-400">{c.status}</div>
              <div className="text-xs text-slate-500">{c.reason_label || c.reason_code?.replace(/_/g," ")}</div>
            </div>
            <div className="text-sm font-semibold text-emerald-300">-{fmtMoney(c.amount)}</div>
          </div>
        ))}
      </div>
    );
  }

  // ── Collections notice sub-panel ───────────────────────────────────────────
  if (tab === "collections") {
    const notice = summary?.collections_notice;
    if (!notice) return (
      <div className="text-slate-500 text-sm py-8 text-center" data-testid="student-collections-clear">
        No active collection cases on your account.
      </div>
    );

    const statusColors = {
      MONITORING: "text-slate-300",
      OPEN:       "text-amber-400",
      DEFAULTED:  "text-red-400",
      SUSPENDED:  "text-amber-300",
    };

    return (
      <div className="space-y-3" data-testid="student-collections-panel">
        <div className="border border-amber-500/40 bg-amber-900/20 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-amber-300 uppercase tracking-wide">Collections Notice</span>
            <span className={`text-xs font-medium ${statusColors[notice.case_status] || "text-slate-400"}`}>
              {notice.case_status?.replace(/_/g," ")}
            </span>
          </div>
          <div className="text-sm font-semibold text-slate-200">
            Collectible Debt: {fmtMoney(notice.collectible_debt)}
          </div>
          {notice.hold_active && (
            <div className="text-xs text-red-400 font-medium">
              Account Hold is active. Please contact the finance office.
            </div>
          )}
          {notice.disclosure && (
            <div className="text-xs text-slate-400">{notice.disclosure}</div>
          )}
          <div className="text-xs text-slate-500">
            This amount reflects your personal liability only (sponsor/government covered portions are excluded).
            Any outstanding promise-to-pay arrangement does not reduce this balance until payment is received.
          </div>
        </div>
      </div>
    );
  }

  return null;
}
